import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Col, OverlayTrigger, Popover, Row } from 'react-bootstrap';
import * as ControlAdminApi from '../../../api/ControlPanelApi';
import * as MinistryExamsApi from '../../../api/MinistryExams';
import { PAGE_LENGTH } from '../../../common/constants';
import { handleApiError } from '../../../common/errorHandler';
import DataTable from '../../../components/DataTable';
import { PageBody } from '../../../components/SiteWrapper';
import PageHeaderActions from '../../../components/General/PageHeaderActions';
import { useRowSpan } from '../../../components/DataTable/useRowSpan';
import { FormFieldSelect } from '../../../components/Form/FormField';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { Link } from 'react-router-dom';

export default function ExamSaveSubStats() {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [examOptions, setExamOptions] = useState([]);
  const defaultFilterValue = {
    exam: '',
  };
  const [dateOptions, setDateOptions] = useState([]);

  const {
    control,
    formState: { errors: examSaveSubErrors },
    watch,
    setValue,
    getValues,
  } = useForm({
    mode: 'onChange',
    defaultValues: defaultFilterValue,
  });
  const selectedExam = watch('exam');

  const [filter, setFilter] = useState({
    _page: 1,
    _limit: 25,
    q: '',
    _sort: 'id',
    _order: 'asc',
    isDelete: null,
    exam_id: null,
    date: '',
    exam_id: '',
  });

  const initialState = {
    sortBy: [{ id: 'id', desc: false }],
    pageSize: filter._limit,
    pageIndex: 0,
  };

  const fetchRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await ControlAdminApi.getControlPanelSaveSubmitExamStats(
        filter
      );

      if (response.data.rrbSubmissionStatus) {
        const formattedData = Object.values(response.data.rrbSubmissionStatus)
          .map((rrb) => {
            return {
              rrb_name: `${rrb.rrb_name} (${rrb.city})`,
              rrb_city: rrb.city,
              total_centers: rrb.total_centers,
              total_submitted: rrb.total_submitted,
              total_saved: rrb.total_saved,
              total_no_action: rrb.total_no_action,
              submittedPeople: rrb.submittedPeople,
              savedPeople: rrb.savedPeople,
              noActionPeople: rrb.noActionPeople,
            };
          })
          // Sort the formatted data by rrb_name in ascending order
          .sort((a, b) => a.rrb_city.localeCompare(b.rrb_city));

        setRows(formattedData);
        setTotalRecords(formattedData.length);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchRecords();
  }, [filter]);

  const getAllMinistryExams = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await MinistryExamsApi.getAll();
      console.log('response', response);
      const exmOptions = [];

      if (response?.data?.records) {
        response.data.records.map((record) => {
          exmOptions.push({
            record,
          });
        });

        setExamOptions(exmOptions);

        const highestIdExam = response.data.records.reduce((max, exam) =>
          exam.id > max.id ? exam : max
        );

        if (highestIdExam) {
          setValue('exam', {
            key: highestIdExam.id,
            value: highestIdExam.id,
            label: highestIdExam.exam_name,
          });

          // Select today's date from ExamSchedule if it exists
          const today = moment().format('YYYY-MM-DD');
          const todaySchedule = highestIdExam?.ExamSchedules?.find(
            (schedule) => moment(schedule.date).format('YYYY-MM-DD') === today
          );

          setFilter((prev) => ({
            ...prev,
            exam_id: highestIdExam.id,
            date: todaySchedule ? today : '',
          }));
        }
      }
    } catch (error) {
      setIsLoading(false);
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getAllMinistryExams();
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: 'RRB Name',
        accessor: 'rrb_name',
        disableSortBy: true,
        Cell: ({ value }) => {
          return (
            <span
              style={{
                padding: '8px 12px',
              }}
            >
              {value}
            </span>
          );
        },
      },
      {
        Header: 'Total Centers',
        accessor: 'total_centers',
        disableSortBy: true,
        Cell: ({ value }) => {
          return (
            <span
              style={{
                padding: '8px 12px',
              }}
            >
              {value}
            </span>
          );
        },
      },
      {
        Header: 'Total Submitted',
        accessor: 'total_submitted',
        // className: 'bg-success-lt',
        disableSortBy: true,
        Cell: ({ row, cell }) => {
          const totalSubmitted = row?.original?.total_submitted ?? 0;
          const submittedPeople = row?.original?.submittedPeople ?? [];
          const total_centers = row?.original?.total_centers ?? 0;
          const rrbName = row?.original?.rrb_name ?? 'Unknown';

          const popoverContent =
            totalSubmitted > 0 ? (
              renderPeople(submittedPeople, rrbName)
            ) : (
              <Popover>
                <Popover.Body>No data available</Popover.Body>
              </Popover>
            );

          const cellStyle = {
            backgroundColor:
              total_centers - totalSubmitted === 0 ? '#50C87850' : '',
            color: total_centers - totalSubmitted === 0 ? 'green' : '',
            textDecoration: totalSubmitted > 0 ? 'underline' : 'none',
            padding: '8px 12px',
            cursor: totalSubmitted > 0 && 'pointer',
          };

          return (
            <div style={cellStyle}>
              <OverlayTrigger
                trigger='click'
                rootClose
                placement='right'
                overlay={popoverContent}
              >
                <span>{totalSubmitted || 0}</span>
              </OverlayTrigger>
            </div>
          );
        },
      },
      {
        Header: 'Total Saved',
        accessor: 'total_saved',
        disableSortBy: true,
        Cell: ({ row }) => {
          const totalSaved = row?.original?.total_saved ?? 0;
          const savedPeople = row?.original?.savedPeople ?? [];
          const rrbName = row?.original?.rrb_name ?? 'Unknown';

          const cellStyle = {
            backgroundColor: totalSaved > 0 ? '#FFDBBB' : '',
            color: totalSaved > 0 ? 'orange' : '',
            textDecoration: totalSaved > 0 ? 'underline' : 'none',
            padding: '8px 12px',
            cursor: totalSaved > 0 && 'pointer',
          };

          return (
            <div style={cellStyle}>
              <OverlayTrigger
                trigger='click'
                rootClose
                placement='right'
                overlay={
                  totalSaved > 0 ? (
                    renderPeople(savedPeople, rrbName)
                  ) : (
                    <Popover>
                      <Popover.Body>No data available</Popover.Body>
                    </Popover>
                  )
                }
              >
                <span>{totalSaved}</span>
              </OverlayTrigger>
            </div>
          );
        },
      },
      {
        Header: 'No Action',
        accessor: 'total_no_action',
        disableSortBy: true,
        Cell: ({ row }) => {
          const total_no_action = row?.original?.total_no_action ?? 0;
          const noActionPeople = row?.original?.noActionPeople ?? [];
          const rrbName = row?.original?.rrb_name ?? 'Unknown';

          const cellStyle = {
            textDecoration: total_no_action > 0 ? 'underline' : 'none',
            padding: '8px 12px',
          };

          return (
            <div style={cellStyle}>
              <OverlayTrigger
                trigger='click'
                rootClose
                placement='right'
                overlay={
                  total_no_action > 0 ? (
                    renderPeople(noActionPeople, rrbName)
                  ) : (
                    <Popover>
                      <Popover.Body>No data available</Popover.Body>
                    </Popover>
                  )
                }
              >
                <span
                  style={{
                    textDecoration: total_no_action > 0 && 'underline',
                    cursor: total_no_action > 0 && 'pointer',
                  }}
                >
                  {total_no_action}
                </span>
              </OverlayTrigger>
            </div>
          );
        },
      },
    ],
    []
  );
  const renderPeople = (peopleData, title) => {
    if (!Array.isArray(peopleData) || peopleData.length === 0) {
      return (
        <Popover>
          <Popover.Body>No data available</Popover.Body>
        </Popover>
      );
    }
    return (
      <Popover>
        <Popover.Header>
          <strong className='h3'>{title}</strong>
          <br />
          <strong className='h5 text-primary'>
            <Link
              to={`/exams/reports/${peopleData[0]?.exam_id}?rrb=${peopleData[0]?.rrb_id}&ed_id=${peopleData[0]?.exam_days_id}`}
            >
              View Detailed Report
            </Link>
          </strong>
        </Popover.Header>
        <Popover.Body>
          {peopleData.map((data) => (
            <div key={data.mobile} className='mb-2'>
              <strong>Name:</strong> {data?.name}
              <br />
              <strong>Mobile:</strong> {data?.mobile}
              <br />
              <strong>Center:</strong> {data?.center}
              <br />
              <Link
                to={`/exams/reports/${data.exam_id}?rrb=${data?.rrb_id}&ed_id=${data?.exam_days_id}&c_id=${data?.center_code}`}
              >
                View Detailed Report
              </Link>
            </div>
          ))}
        </Popover.Body>
      </Popover>
    );
  };

  useEffect(() => {
    if (examOptions.length > 0) {
      const selectedExm = examOptions.find(
        (exm) => Number(exm.record.id) === Number(selectedExam.key)
      );

      const dates = selectedExm?.record?.ExamSchedules.map((schedule) => ({
        key: schedule?.date,
        label: moment(schedule?.date).format('DD/MM/YYYY'),
      })).sort((a, b) => moment(a.key).valueOf() - moment(b.key).valueOf()); // Sort by date

      setDateOptions(dates || []);
    }
  }, [selectedExam, examOptions]);

  useEffect(() => {
    setFilter((prev) => ({
      ...prev, // Spread the previous state
      exam_id: selectedExam.key, // Update only the exam_id field
    }));
    setFilter((prev) => ({ ...prev, date: '' }));
  }, [selectedExam]); // This will run every time selectedExam changes

  const defaultOption = (() => {
    const matchedExam = dateOptions?.find(({ key, label }) => {
      return moment(key).format('DD/MM/YYYY') === moment().format('DD/MM/YYYY');
    });

    return matchedExam
      ? {
          key: matchedExam.key,
          label: moment(matchedExam.date).format('DD/MM/YYYY'),
        }
      : { key: null, label: moment().format('DD/MM/YYYY') };
  })();

  return (
    <PageBody>
      <Row className='d-flex flex-row-reverse mb-2'>
        <Col md='2'>
          <FormFieldSelect
            name='exam'
            control={control}
            options={examOptions?.map((exm) => ({
              key: exm.record.id,
              value: exm.record.id,
              label: exm.record.exam_name, // Assuming `exam_name` is the field in the record
            }))}
            error={examSaveSubErrors?.exam}
            size='12'
            placeholder='Select Exam'
            defaultValue={selectedExam} // Preselect the exam based on the `selectedExam`
            onChange={(value) => setValue('exam', value)} // Ensure the value is updated when selected
          />
        </Col>
      </Row>
      <DataTable
        header={<PageHeaderActions title='RRB Wise Exam Save/Submit Stats' />}
        columns={columns}
        data={rows}
        initialState={initialState}
        totalRecords={totalRecords}
        setFilter={setFilter}
        tableHooks={(hooks) => hooks.useInstance.push(useRowSpan)}
        setSelectState={(date) => {
          console.log('date', date);
          setFilter((prev) => ({ ...prev, date: date }));
        }}
        defaultPageLength={[25, 50, 100]}
        isLoading={isLoading}
        manual={true}
        align='table-vcenter'
        hidePagination
        extraSelectOptions={dateOptions}
        defaultOption={defaultOption}
        extraSelectFilter={true}
        customClass='custom-table-padding-zero'
      />
    </PageBody>
  );
}
